import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Store, 
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  RefreshCw,
  Download,
  Target,
  FileSpreadsheet,
  Users,
  ShoppingCart,
  Percent,
  RotateCcw,
  Award,
  XCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import TranslatedText from '../components/TranslatedText';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const StorePulseDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const { languageConfig } = useLanguage();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/agent-dashboards/store-pulse`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load StorePulse dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async (dataType, label) => {
    setExporting(dataType);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/agent-dashboards/store-pulse/export`,
        {
          params: { data_type: dataType },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dataType}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      toast.success(`${label} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${label}`);
    } finally {
      setExporting(null);
    }
  };

  const handleExportAll = async (dataType, label) => {
    setExporting(dataType);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/agent-dashboards/store-pulse/export-all`,
        {
          params: { data_type: dataType },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `store_pulse_${dataType}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      toast.success(`${label} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${label}`);
    } finally {
      setExporting(null);
    }
  };

  const formatCurrency = (value) => {
    if (typeof value === 'string') return value;
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)} K`;
    return `₹${value?.toFixed(0) || 0}`;
  };

  const formatNumber = (value) => {
    if (typeof value === 'string') return value;
    return value?.toLocaleString() || '0';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="store-pulse-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!data?.hasData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4" data-testid="store-pulse-no-data">
        <Store className="h-16 w-16 text-slate-400" />
        <h2 className="text-xl font-semibold text-slate-700">No Analysis Available</h2>
        <p className="text-slate-500">{data?.message || 'Please run the ReasonStorePulse agent first.'}</p>
        <Button onClick={() => window.location.href = '/agents'}>Go to Agents</Button>
      </div>
    );
  }

  const overview = data?.storeOverview || {};
  const storeScorecard = data?.storeScorecard || [];
  const topStores = data?.topStores || [];
  const bottomStores = data?.bottomStores || [];
  const conversionAnalysis = data?.conversionAnalysis || {};
  const productivityMetrics = data?.productivityMetrics || [];
  const returnAnalysis = data?.returnAnalysis || {};
  const recommendations = data?.recommendations || [];
  const actionPlan = data?.actionPlan || {};

  const COLORS = ['#6366f1', '#22c55e', '#3b82f6', '#f97316', '#ef4444', '#8b5cf6'];

  return (
    <div className="space-y-6" data-testid="store-pulse-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Store className="h-8 w-8 text-indigo-600" />
            <TranslatedText text="StorePulse Dashboard" cacheKey="storepulse:title" />
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text="Store Execution & Operational Intelligence" cacheKey="storepulse:subtitle" />
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExportAll('all_analysis', 'Full Analysis')}>
            <Download className="h-4 w-4 mr-2" />Export All
          </Button>
        </div>
      </div>

      {/* Executive Summary */}
      <Card className="bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-900/20 border-indigo-200">
        <CardHeader><CardTitle><TranslatedText text="Executive Summary" cacheKey="storepulse:execsummary" /></CardTitle></CardHeader>
        <CardContent>
          <p className="text-slate-700 dark:text-slate-300 whitespace-pre-line" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text={data?.summary} cacheKey="storepulse:summary" />
          </p>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <CardContent className="pt-4">
            <p className="text-xs text-indigo-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Total Stores" cacheKey="storepulse:total_stores" /></p>
            <p className="text-xl font-bold text-indigo-900">{formatNumber(overview.totalStores)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4">
            <p className="text-xs text-green-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Top Performers" cacheKey="storepulse:top_performers" /></p>
            <p className="text-xl font-bold text-green-900">{topStores.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-4">
            <p className="text-xs text-red-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Underperforming" cacheKey="storepulse:underperforming" /></p>
            <p className="text-xl font-bold text-red-900">{bottomStores.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <p className="text-xs text-blue-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Avg Conversion" cacheKey="storepulse:avg_conversion" /></p>
            <p className="text-xl font-bold text-blue-900">{conversionAnalysis.avgConversionRate || overview.avgConversion || 'N/A'}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-4">
            <p className="text-xs text-purple-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Avg Revenue/Store" cacheKey="storepulse:avg_revenue" /></p>
            <p className="text-xl font-bold text-purple-900">{formatCurrency(overview.avgRevenuePerStore)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="pt-4">
            <p className="text-xs text-orange-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Return Rate" cacheKey="storepulse:return_rate" /></p>
            <p className="text-xl font-bold text-orange-900">{returnAnalysis.overallReturnRate || overview.returnRate || 'N/A'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Top 5 Recommendations */}
      <Card className="border-indigo-200 bg-indigo-50/30">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-indigo-800">
              <Target className="h-5 w-5" />
              <TranslatedText text="Top 5 Store Recommendations" cacheKey="storepulse:recommendations_title" />
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => handleExportAll('recommendations', 'All Recommendations')} disabled={exporting === 'recommendations'}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />Download All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recommendations.slice(0, 5).map((rec, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg border">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                  idx === 0 ? 'bg-indigo-500' : idx === 1 ? 'bg-blue-500' : 'bg-slate-500'
                }`}>{idx + 1}</span>
                <div className="flex-1">
                  <p className="font-medium">{rec.description || rec.action || rec.text || rec}</p>
                  {rec.expectedImpact && <p className="text-sm text-indigo-600 mt-1">Impact: {rec.expectedImpact}</p>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Store Performance Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Stores */}
        <Card className="border-green-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-green-700 flex items-center gap-2">
                <Award className="h-5 w-5" />
                Top Performing Stores
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => handleExport('top_stores', 'Top Stores')} disabled={exporting === 'top_stores'}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {topStores.slice(0, 10).map((store, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center text-green-700 font-bold text-sm">
                      {idx + 1}
                    </span>
                    <div>
                      <p className="font-medium">{store.storeName || store.storeId}</p>
                      <p className="text-xs text-slate-500">{store.region || store.cluster || ''}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">{formatCurrency(store.revenue)}</p>
                    {store.conversion && <p className="text-xs text-slate-500">Conv: {store.conversion}</p>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Bottom Stores */}
        <Card className="border-red-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-red-700 flex items-center gap-2">
                <XCircle className="h-5 w-5" />
                Underperforming Stores
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => handleExport('bottom_stores', 'Bottom Stores')} disabled={exporting === 'bottom_stores'}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {bottomStores.slice(0, 10).map((store, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-red-200 flex items-center justify-center text-red-700 font-bold text-sm">
                      !
                    </span>
                    <div>
                      <p className="font-medium">{store.storeName || store.storeId}</p>
                      <p className="text-xs text-red-600">{store.issue || store.reason || 'Below target'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-red-600">{formatCurrency(store.revenue)}</p>
                    {store.gap && <p className="text-xs text-red-500">Gap: {store.gap}</p>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Conversion Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Percent className="h-5 w-5" />
            Conversion Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-700">Avg Conversion</p>
              <p className="text-xl font-bold text-blue-900">{conversionAnalysis.avgConversionRate || 'N/A'}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-xs text-green-700">Best Conversion</p>
              <p className="text-xl font-bold text-green-900">{conversionAnalysis.bestConversion || 'N/A'}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg">
              <p className="text-xs text-red-700">Worst Conversion</p>
              <p className="text-xl font-bold text-red-900">{conversionAnalysis.worstConversion || 'N/A'}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="text-xs text-purple-700">Potential Revenue</p>
              <p className="text-xl font-bold text-purple-900">{formatCurrency(conversionAnalysis.potentialRevenue) || 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Return Analysis */}
      {Object.keys(returnAnalysis).length > 0 && (
        <Card className="border-orange-200">
          <CardHeader>
            <CardTitle className="text-orange-700 flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              Return Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-orange-50 rounded-lg">
                <p className="text-xs text-orange-700">Overall Return Rate</p>
                <p className="text-xl font-bold text-orange-900">{returnAnalysis.overallReturnRate || 'N/A'}</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg">
                <p className="text-xs text-orange-700">Return Value</p>
                <p className="text-xl font-bold text-orange-900">{formatCurrency(returnAnalysis.totalReturnValue)}</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg">
                <p className="text-xs text-orange-700">High Return Stores</p>
                <p className="text-xl font-bold text-orange-900">{returnAnalysis.highReturnStores || 'N/A'}</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg">
                <p className="text-xs text-orange-700">Top Return Reason</p>
                <p className="text-xl font-bold text-orange-900">{returnAnalysis.topReason || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Store Scorecard */}
      {storeScorecard.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Store Scorecard</CardTitle>
              <Button size="sm" variant="outline" onClick={() => handleExport('store_scorecard', 'Store Scorecard')} disabled={exporting === 'store_scorecard'}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Store</th>
                    <th className="px-3 py-2 text-right font-medium">Revenue</th>
                    <th className="px-3 py-2 text-right font-medium">Footfall</th>
                    <th className="px-3 py-2 text-right font-medium">Conversion</th>
                    <th className="px-3 py-2 text-right font-medium">ATV</th>
                    <th className="px-3 py-2 text-center font-medium">Score</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {storeScorecard.slice(0, 10).map((store, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium">{store.storeName || store.storeId}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(store.revenue)}</td>
                      <td className="px-3 py-2 text-right">{formatNumber(store.footfall)}</td>
                      <td className="px-3 py-2 text-right">{store.conversion || '-'}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(store.atv)}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          (store.score || 0) >= 80 ? 'bg-green-100 text-green-700' :
                          (store.score || 0) >= 60 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {store.score || '-'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Plan */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Prioritized Action Plan</CardTitle>
            <Button size="sm" variant="outline" onClick={() => handleExportAll('action_plan', 'Action Plan')} disabled={exporting === 'action_plan'}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-red-800 mb-3">P0 - Immediate</h4>
              <ul className="space-y-2 text-sm">
                {(actionPlan.P0_Immediate || []).slice(0, 3).map((action, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    <span>{action.action || action}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
              <h4 className="font-bold text-yellow-800 mb-3">P1 - This Week</h4>
              <ul className="space-y-2 text-sm">
                {(actionPlan.P1_ThisWeek || []).slice(0, 3).map((action, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-yellow-500 mt-1">•</span>
                    <span>{action.action || action}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
              <h4 className="font-bold text-green-800 mb-3">P2 - Next Cycle</h4>
              <ul className="space-y-2 text-sm">
                {(actionPlan.P2_NextCycle || []).slice(0, 3).map((action, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">•</span>
                    <span>{action.action || action}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export All Stores */}
      <Card className="bg-slate-50">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Export Full Store Data</h3>
              <p className="text-sm text-slate-500">Download complete store master for analysis</p>
            </div>
            <Button onClick={() => handleExport('all_stores', 'All Stores')} disabled={exporting === 'all_stores'}>
              <Download className="h-4 w-4 mr-2" />
              {exporting === 'all_stores' ? 'Exporting...' : 'Export All Stores'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StorePulseDashboard;
